import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import dataCache from "./DataCache";

// Loading states
const LOADING_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

// Loading priorities
const LOADING_PRIORITIES = {
  CRITICAL: "critical", // Blocks UI, shows spinner
  NORMAL: "normal", // Shows skeleton/loading state
  BACKGROUND: "background", // Silent loading
};

// Action types
const ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_SUCCESS: "SET_SUCCESS",
  SET_ERROR: "SET_ERROR",
  CLEAR_LOADING: "CLEAR_LOADING",
  CLEAR_ALL: "CLEAR_ALL",
};

// Initial state
const initialState = {
  loadingStates: {}, // { key: { state, priority, error, startTime } }
  requestCount: 0,
};

// Reducer
function loadingReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.key]: {
            state: LOADING_STATES.LOADING,
            priority: action.priority,
            error: null,
            startTime: Date.now(),
          },
        },
        requestCount: state.requestCount + 1,
      };

    case ACTIONS.SET_SUCCESS:
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.key]: {
            ...state.loadingStates[action.key],
            state: LOADING_STATES.SUCCESS,
            error: null,
          },
        },
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.key]: {
            ...state.loadingStates[action.key],
            state: LOADING_STATES.ERROR,
            error: action.error,
          },
        },
      };

    case ACTIONS.CLEAR_LOADING:
      const newStates = { ...state.loadingStates };
      delete newStates[action.key];
      return {
        ...state,
        loadingStates: newStates,
      };

    case ACTIONS.CLEAR_ALL:
      return {
        ...initialState,
      };

    default:
      return state;
  }
}

// Context
const LoadingManagerContext = createContext();

// Provider component
export const LoadingManagerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(loadingReducer, initialState);

  // Set loading state
  const setLoading = useCallback(
    (key, priority = LOADING_PRIORITIES.NORMAL) => {
      dispatch({
        type: ACTIONS.SET_LOADING,
        key,
        priority,
      });
    },
    []
  );

  // Set success state
  const setSuccess = useCallback((key, data = null) => {
    dispatch({
      type: ACTIONS.SET_SUCCESS,
      key,
    });

    // Auto-clear after 1 second to prevent memory buildup
    setTimeout(() => {
      dispatch({
        type: ACTIONS.CLEAR_LOADING,
        key,
      });
    }, 1000);
  }, []);

  // Set error state
  const setError = useCallback((key, error) => {
    dispatch({
      type: ACTIONS.SET_ERROR,
      key,
      error,
    });
  }, []);

  // Clear specific loading state
  const clearLoading = useCallback((key) => {
    dispatch({
      type: ACTIONS.CLEAR_LOADING,
      key,
    });
  }, []);

  // Clear all loading states
  const clearAll = useCallback(() => {
    dispatch({
      type: ACTIONS.CLEAR_ALL,
    });
  }, []);

  // Check if specific key is loading
  const isLoading = useCallback(
    (key) => {
      const loadingState = state.loadingStates[key];
      return loadingState?.state === LOADING_STATES.LOADING;
    },
    [state.loadingStates]
  );

  // Check if any critical loading is active
  const isCriticalLoading = useCallback(() => {
    return Object.values(state.loadingStates).some(
      (loadingState) =>
        loadingState.state === LOADING_STATES.LOADING &&
        loadingState.priority === LOADING_PRIORITIES.CRITICAL
    );
  }, [state.loadingStates]);

  // Get loading state for key
  const getLoadingState = useCallback(
    (key) => {
      return state.loadingStates[key] || { state: LOADING_STATES.IDLE };
    },
    [state.loadingStates]
  );

  // Get all loading states by priority
  const getLoadingByPriority = useCallback(
    (priority) => {
      return Object.entries(state.loadingStates)
        .filter(([_, loadingState]) => loadingState.priority === priority)
        .map(([key, loadingState]) => ({ key, ...loadingState }));
    },
    [state.loadingStates]
  );

  // Get loading statistics
  const getStats = useCallback(() => {
    const loadingCount = Object.values(state.loadingStates).filter(
      (loadingState) => loadingState.state === LOADING_STATES.LOADING
    ).length;

    const errorCount = Object.values(state.loadingStates).filter(
      (loadingState) => loadingState.state === LOADING_STATES.ERROR
    ).length;

    return {
      total: Object.keys(state.loadingStates).length,
      loading: loadingCount,
      errors: errorCount,
      requests: state.requestCount,
    };
  }, [state.loadingStates, state.requestCount]);

  // Enhanced loading wrapper with caching
  const loadWithCache = useCallback(
    async (
      key,
      fetcher,
      cacheKey = null,
      priority = LOADING_PRIORITIES.NORMAL,
      options = {}
    ) => {
      const {
        skipCache = false,
        cacheTTL = null,
        retries = 1,
        retryDelay = 1000,
      } = options;

      // Check cache first (unless skipping)
      if (!skipCache && cacheKey) {
        const cachedData = dataCache.get(cacheKey);
        if (cachedData) {
          console.log(`🎯 Using cached data for ${key}`);
          return cachedData;
        }
      }

      // Start loading
      setLoading(key, priority);

      let lastError = null;

      // Retry logic
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const data = await fetcher();

          // Cache the result
          if (cacheKey && data) {
            if (cacheTTL) {
              dataCache.set(cacheKey, data, cacheTTL);
            } else {
              dataCache.set(cacheKey, data);
            }
          }

          setSuccess(key, data);
          return data;
        } catch (error) {
          lastError = error;

          // If not last attempt, wait before retry
          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }

      // All retries failed
      setError(key, lastError);
      throw lastError;
    },
    [setLoading, setSuccess, setError]
  );

  // FIXED: Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      // State
      loadingStates: state.loadingStates,

      // Actions
      setLoading,
      setSuccess,
      setError,
      clearLoading,
      clearAll,

      // Queries
      isLoading,
      isCriticalLoading,
      getLoadingState,
      getLoadingByPriority,
      getStats,

      // Enhanced loading
      loadWithCache,

      // Constants
      LOADING_STATES,
      LOADING_PRIORITIES,
    }),
    [
      state.loadingStates,
      setLoading,
      setSuccess,
      setError,
      clearLoading,
      clearAll,
      isLoading,
      isCriticalLoading,
      getLoadingState,
      getLoadingByPriority,
      getStats,
      loadWithCache,
    ]
  );

  return (
    <LoadingManagerContext.Provider value={value}>
      {children}
    </LoadingManagerContext.Provider>
  );
};

// Hook to use loading manager
export const useLoadingManager = () => {
  const context = useContext(LoadingManagerContext);
  if (!context) {
    throw new Error(
      "useLoadingManager must be used within LoadingManagerProvider"
    );
  }
  return context;
};

// Export constants for external use
export { LOADING_STATES, LOADING_PRIORITIES };
